import { useEffect, useRef, useState } from "react";
import { useAchievements } from "../../hooks/useAchievements";
import { Achievement } from "../../utils/achievements";
import AchievementUnlockedToast from "./AchievementUnlockedToast";
import * as analytics from "../../utils/analytics";

// Mounted once in the header (present on every page) so an achievement can
// be announced no matter which page the qualifying action happens on. Marks
// each achievement seen as soon as it's queued to show, not when its toast
// times out - so navigating away right after unlocking one still prevents
// it from re-announcing on the next page.
function AchievementUnlockedNotice() {
    const { newlyUnlocked, isReady, markSeen } = useAchievements();
    const [queue, setQueue] = useState<Achievement[]>([]);
    const announcedRef = useRef(new Set<string>());

    useEffect(() => {
        if (!isReady || newlyUnlocked.length === 0) return;
        const fresh = newlyUnlocked.filter((achievement) => !announcedRef.current.has(achievement.id));
        if (!fresh.length) return;
        fresh.forEach((achievement) => {
            announcedRef.current.add(achievement.id);
            markSeen(achievement.id);
            analytics.track("achievement_unlocked", { achievement_id: achievement.id });
        });
        setQueue((current) => [...current, ...fresh]);
    }, [isReady, newlyUnlocked, markSeen]);

    const current = queue[0];
    if (!current) return null;

    return (
        <AchievementUnlockedToast
            key={current.id}
            achievement={current}
            onDismiss={() => setQueue((current) => current.slice(1))}
        />
    );
}

export default AchievementUnlockedNotice;
